/**
 * Smooth Scroll
 *
 * Provides smooth animated scrolling for anchor links with optional offset.
 * Automatically intercepts anchor link clicks, animates scroll with GSAP,
 * and handles accessibility (focus management, keyboard navigation).
 *
 * Features:
 * - Auto-detects and handles all anchor links (a[href^="#"])
 * - Dynamic offset via proxy element's computed CSS top value
 * - GSAP animation with reduced motion support
 * - Keyboard navigation (Enter/Space on links)
 * - Focus management for screen readers
 * - Disables Webflow's native scroll behavior
 * - Triggers scroll-based animations on init
 *
 * Structure:
 *   Optional offset proxy element with CSS top value:
 *   <div data-hs-scroll-offset="proxy" style="top: var(--_hs---scroll-offset)"></div>
 */

import { getGsap, prefersReducedMotion, querySelector } from '@utils';

// Type declaration for jQuery (runtime dependency)
declare const $: any;

interface ModuleConfig {
  attributes: {
    elements: {
      'offset-proxy': {
        primary: string;
        aliases: string[];
      };
    };
  };
}

export async function init(config: ModuleConfig) {
  // Store event handlers for cleanup
  let clickHandler: ((e: Event) => void) | null = null;
  let keydownHandler: ((e: KeyboardEvent) => void) | null = null;

  // Disable Webflow's native scroll behavior (jQuery-based)
  // This runs immediately since default module already waits for IX3
  if (typeof $ !== 'undefined') {
    $(document).off('click.wf-scroll');
  }

  // Disable CSS smooth scrolling
  document.documentElement.style.scrollBehavior = 'auto';
  document.body.style.scrollBehavior = 'auto';

  function getScrollOffset(): number {
    // Find element with data-hs-scroll-offset="proxy" and read its computed top value
    const proxyElement = querySelector(config, 'offset-proxy');
    console.log('[smooth-scroll] Proxy element:', proxyElement);
    if (proxyElement && proxyElement instanceof HTMLElement) {
      const computedTop = getComputedStyle(proxyElement).top;
      const offset = parseFloat(computedTop) || 0;
      console.log('[smooth-scroll] Computed top:', computedTop, '-> offset:', offset);
      return offset;
    }
    console.log('[smooth-scroll] No proxy element found, using offset: 0');
    return 0;
  }

  // Smooth scroll to element with offset
  function scrollToElement(target: HTMLElement, offset = 0): void {
    if (!target) return;

    const gsapLib = getGsap('smooth-scroll', false);

    // Skip animation if user prefers reduced motion or GSAP not available
    if (prefersReducedMotion() || !gsapLib) {
      const targetPosition = target.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo(0, targetPosition);
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
      return;
    }

    gsapLib.gsap.to(window, {
      duration: 1,
      scrollTo: {
        y: target,
        offsetY: offset,
      },
      ease: 'power2.out',
      onComplete: function () {
        target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      },
    });
  }

  function handleAnchorActivation(e: Event): void {
    const link = (e.target as Element).closest('a[href^="#"]');
    if (!link) return;

    const href = link.getAttribute('href');
    if (!href || href === '#') return;

    const targetId = href.substring(1);
    const targetElement = document.getElementById(targetId);

    if (targetElement) {
      e.preventDefault();
      if (history.replaceState) {
        history.replaceState(null, null, `#${targetElement.id}`);
      }
      const offset = getScrollOffset();
      scrollToElement(targetElement, offset);
    }
  }

  // Handle anchor link clicks and keyboard activation
  function handleAnchorClicks(): void {
    clickHandler = handleAnchorActivation;
    keydownHandler = (e: KeyboardEvent): void => {
      if (e.key === 'Enter' || e.key === ' ') {
        handleAnchorActivation(e);
      }
    };

    document.addEventListener('click', clickHandler);
    document.addEventListener('keydown', keydownHandler);
  }

  // Initialize anchor link handling
  handleAnchorClicks();

  return {
    result: 'autoInit-smooth-scroll initialized',
    destroy: () => {
      // Remove event listeners
      if (clickHandler) {
        document.removeEventListener('click', clickHandler);
        clickHandler = null;
      }
      if (keydownHandler) {
        document.removeEventListener('keydown', keydownHandler);
        keydownHandler = null;
      }

      // Re-enable CSS smooth scrolling
      document.documentElement.style.scrollBehavior = '';
      document.body.style.scrollBehavior = '';
    },
  };
}
